/**
 * useEmployee Hook
 * Custom hook for managing single employee (fetch, create, update)
 */

import { useState, useEffect, useCallback } from 'react';
import { Employee, CreateEmployeeRequest, UpdateEmployeeRequest } from '../types/employee';
import employeeService from '../services/employeeService';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

interface UseEmployeeResult {
  employee: Employee | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  createEmployee: (data: CreateEmployeeRequest) => Promise<Employee | null>;
  updateEmployee: (id: number, data: UpdateEmployeeRequest) => Promise<Employee | null>;
  activateEmployee: (id: number) => Promise<boolean>;
  suspendEmployee: (id: number) => Promise<boolean>;
  putOnLeave: (id: number) => Promise<boolean>;
  uploadAvatar: (id: number, file: File) => Promise<string | null>;
  refresh: () => void;
}

export const useEmployee = (id?: number, includeRelations = true): UseEmployeeResult => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployee = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await employeeService.getEmployeeById(id, includeRelations);
      setEmployee(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao carregar funcionário';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, includeRelations, enqueueSnackbar]);

  useEffect(() => {
    if (id) {
      fetchEmployee();
    }
  }, [id, fetchEmployee]);

  const createEmployee = useCallback(async (data: CreateEmployeeRequest): Promise<Employee | null> => {
    try {
      setSaving(true);
      setError(null);
      
      const newEmployee = await employeeService.createEmployee(data);
      
      enqueueSnackbar('Funcionário cadastrado com sucesso', { variant: 'success' });
      
      // Navigate to employee list or details
      navigate('/employees');
      
      return newEmployee;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao cadastrar funcionário';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return null;
    } finally {
      setSaving(false);
    }
  }, [enqueueSnackbar, navigate]);

  const updateEmployee = useCallback(async (
    employeeId: number, 
    data: UpdateEmployeeRequest
  ): Promise<Employee | null> => {
    try {
      setSaving(true);
      setError(null);
      
      const updatedEmployee = await employeeService.updateEmployee(employeeId, data);
      
      setEmployee(updatedEmployee);
      enqueueSnackbar('Funcionário atualizado com sucesso', { variant: 'success' });
      
      // Navigate to employee list
      navigate('/employees');
      
      return updatedEmployee;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar funcionário';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return null;
    } finally {
      setSaving(false);
    }
  }, [enqueueSnackbar, navigate]);

  const activateEmployee = useCallback(async (employeeId: number): Promise<boolean> => {
    try {
      const updatedEmployee = await employeeService.activateEmployee(employeeId);
      setEmployee(updatedEmployee);
      enqueueSnackbar('Funcionário ativado com sucesso', { variant: 'success' });
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao ativar funcionário';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar]);

  const suspendEmployee = useCallback(async (employeeId: number): Promise<boolean> => {
    try {
      const updatedEmployee = await employeeService.suspendEmployee(employeeId);
      setEmployee(updatedEmployee);
      enqueueSnackbar('Funcionário suspenso com sucesso', { variant: 'success' });
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao suspender funcionário';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar]);

  const putOnLeave = useCallback(async (employeeId: number): Promise<boolean> => {
    try {
      const updatedEmployee = await employeeService.putOnLeave(employeeId);
      setEmployee(updatedEmployee);
      enqueueSnackbar('Funcionário afastado com sucesso', { variant: 'success' });
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao afastar funcionário';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar]);

  const uploadAvatar = useCallback(async (employeeId: number, file: File): Promise<string | null> => {
    try {
      const { avatarUrl } = await employeeService.uploadAvatar(employeeId, file);
      
      // Refresh employee data to get updated avatar
      if (employee && employee.id === employeeId) {
        fetchEmployee();
      }
      
      enqueueSnackbar('Foto atualizada com sucesso', { variant: 'success' });
      return avatarUrl;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao fazer upload da foto';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return null;
    }
  }, [employee, enqueueSnackbar, fetchEmployee]);

  const refresh = useCallback(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  return {
    employee,
    loading,
    error,
    saving,
    createEmployee,
    updateEmployee,
    activateEmployee,
    suspendEmployee,
    putOnLeave,
    uploadAvatar,
    refresh
  };
};
